import { Bell, CircleHelp, LogOut, Menu } from "lucide-react"
import * as React from "react"
import { useLocation } from "react-router-dom"

import { crumbsFor } from "@/components/layout/nav"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { InfoTip } from "@/components/ui/tooltip"
import { type Cabinets, getCabinets, selectCabinet } from "@/lib/api"
import { useAuth } from "@/lib/auth"

/**
 * Шапка образца: слева крошки, справа — служебные иконки и учётная запись.
 *
 * Уведомления и справка декоративные: за ними нет ни уведомлений, ни справки, —
 * поэтому они помечены как неактивные и объясняют это подсказкой, а не молча
 * ничего не делают по клику. Третья иконка образца — профиль — заменена
 * настоящей учётной записью (AMEND-8 §3 шаг 7): имя аккаунта, почта вошедшего
 * и выход.
 *
 * Имя **аккаунта**, а не только человека, стоит здесь не для красоты: в системе
 * несколько арендаторов, и почта одного человека может быть заведена в двух.
 * Экран, не отвечающий на вопрос «в чьих данных я сейчас», — это способ
 * поменять правило не тому клиенту.
 *
 * На узком экране слева добавляется «гамбургер»: там левое меню — выдвижная
 * шторка, и открыть её больше нечем. На `md+` кнопка скрыта, меню и так в потоке.
 */
export function Topbar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { pathname } = useLocation()
  const crumbs = crumbsFor(pathname)

  return (
    <header className="flex h-10 shrink-0 items-center justify-between gap-2 border-b border-line bg-surface px-4">
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          onClick={onOpenMenu}
          aria-label="Open menu"
          className="-ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded text-fg-muted hover:bg-surface-sunken hover:text-fg md:hidden"
        >
          <Menu className="h-4 w-4" strokeWidth={1.75} />
        </button>
        <nav aria-label="Breadcrumb" className="min-w-0">
          {/*
           * `min-w-0` на `ol` и `li` — не украшение: без него flex-элемент не
           * сжимается ниже своего содержимого, `truncate` не срабатывает, и на
           * 360px «Scoring Engine / Custom rules» уезжал прямо под колокольчик
           * (замерено: крошка до 189px при иконках с 170px). Родительские
           * крошки на телефоне скрыты целиком вместе со своими разделителями:
           * путь там дублирует заголовок страницы, а место дороже.
           */}
          <ol className="flex min-w-0 items-center gap-1.5 text-[13px] text-fg-muted">
            {crumbs.map((crumb, index) => {
              const last = index === crumbs.length - 1
              return (
                <li
                  key={crumb}
                  className={
                    last
                      ? "flex min-w-0 items-center gap-1.5"
                      : "hidden min-w-0 items-center gap-1.5 md:flex"
                  }
                >
                  {index > 0 && <span className="hidden shrink-0 text-fg-subtle md:inline">/</span>}
                  <span className={last ? "truncate text-fg" : "truncate"}>{crumb}</span>
                </li>
              )
            })}
          </ol>
        </nav>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {[
          { icon: Bell, label: "Notifications" },
          { icon: CircleHelp, label: "Help" },
        ].map(({ icon: Icon, label }) => (
          <InfoTip key={label} label={`${label} — not part of this console`}>
            <span
              aria-disabled
              className="flex h-7 w-7 cursor-not-allowed items-center justify-center rounded text-fg-subtle"
            >
              <Icon className="h-4 w-4" strokeWidth={1.75} />
            </span>
          </InfoTip>
        ))}
        <CabinetSwitcher />
        <AccountChip />
      </div>
    </header>
  )
}

/**
 * Кто и в каком аккаунте работает, плюс выход.
 *
 * Почта прячется на узком экране, имя аккаунта — нет: на телефоне место дороже,
 * но вопрос «чей это аккаунт» важнее вопроса «под кем я вошёл».
 */
function AccountChip() {
  const { identity, signOut } = useAuth()
  const [leaving, setLeaving] = React.useState(false)

  if (!identity) return null

  return (
    <div className="ml-1.5 flex items-center gap-2 border-l border-line pl-2.5">
      <div className="flex min-w-0 flex-col items-end leading-tight">
        <span className="max-w-[12rem] truncate text-sm font-medium text-fg">
          {identity.account_name}
        </span>
        <span className="hidden max-w-[14rem] truncate text-xs text-fg-muted sm:block">
          {identity.email}
        </span>
      </div>
      <InfoTip label="Sign out">
        <button
          type="button"
          // Выход не спрашивает подтверждения: ошибочный клик стоит одного
          // повторного входа, а лишнее окно «точно выйти?» на общем компьютере
          // стоит незакрытой сессии.
          onClick={() => {
            setLeaving(true)
            void signOut().finally(() => setLeaving(false))
          }}
          disabled={leaving}
          aria-label="Sign out"
          className="flex h-7 w-7 items-center justify-center rounded text-fg-muted transition-colors duration-150 ease-snap hover:bg-surface-sunken hover:text-fg disabled:opacity-45"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </InfoTip>
    </div>
  )
}

/**
 * Переключатель подкабинета (AMEND-121): чьи транзакции, дашборд и списки сейчас
 * на экране. Список и проверку «подкабинет — свой» держит сервер; здесь только
 * выбор. После выбора страница перезагружается целиком: каждый экран заново
 * спросит свои данные уже в новом подкабинете, и ни один не покажет старые.
 *
 * Один подкабинет — переключать нечего, переключатель не рисуется. Негодная
 * кука (`selected = null`) молча меняется на подкабинет по умолчанию.
 */
function CabinetSwitcher() {
  const { identity } = useAuth()
  const [cabinets, setCabinets] = React.useState<Cabinets | null>(null)
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    if (!identity) return
    const controller = new AbortController()
    getCabinets(controller.signal)
      .then((found) => {
        const fallback = found.items[0]?.id
        if (found.selected === null && fallback != null) {
          void selectCabinet(fallback).then(() => window.location.reload())
          return
        }
        setCabinets(found)
      })
      .catch(() => {
        /* без списка остаётся подкабинет по умолчанию — данные всё равно его */
      })
    return () => controller.abort()
  }, [identity])

  if (!cabinets || cabinets.items.length < 2) return null

  return (
    <Select
      value={String(cabinets.selected)}
      disabled={busy}
      onValueChange={(value) => {
        setBusy(true)
        selectCabinet(Number(value))
          .then(() => window.location.reload())
          .catch(() => setBusy(false))
      }}
    >
      <SelectTrigger aria-label="Cabinet" className="h-7 max-w-[10rem] sm:max-w-[16rem]">
        <span className="truncate">
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent align="end">
        {cabinets.items.map((cabinet) => (
          <SelectItem key={String(cabinet.id)} value={String(cabinet.id)}>
            {cabinet.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
